import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { getChannels } from 'src/features/chats/api/use-channel-by-members'
import { useChatClient } from 'src/features/chats/context/chat-context'
import { useCreateSneakChannel } from 'src/features/chats/sub-features/sneak/api/use-create-sneak-channel'
import { ConfirmSneakDialog } from 'src/features/chats/sub-features/sneak/components/confirm-sneak-dialog'
import { getChatType } from 'src/features/chats/utils/get-chat-type'
import { logger } from 'src/lib/logger'
import { ChatFilled } from 'src/shared/components/icons/chat/chat-filled'
import { Button } from 'src/shared/components/ui/buttons/button'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useUserById } from 'src/shared/features/users/api/accounts/use-user-by-id'
import { type ChatType } from 'src/shared/schemas/chats/chat-data'

interface ChatChannel {
  chatType: ChatType
  channelId: string
}

export const UserChatButton = ({ userId }: { userId: number }) => {
  const navigate = useNavigate()
  const { client, isConnecting, reconnect } = useChatClient()
  const { data: currentUser } = useMyUserData()
  const { data: targetUser } = useUserById(userId)
  const createSneakMutation = useCreateSneakChannel()
  const [channels, setChannels] = useState<ChatChannel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showSneakConfirmation, setShowSneakConfirmation] = useState(false)

  const fetchChannels = useCallback(async () => {
    if (!client || !currentUser?.id) {
      return
    }

    if (isConnecting) {
      return
    }

    try {
      setIsLoading(true)
      const channelList = await getChannels(client, String(currentUser.id), String(userId))

      // Reset channels before adding new ones
      setChannels([])

      if (channelList) {
        const newChannels: ChatChannel[] = []
        for (const channel of channelList) {
          if (!channel.id) {
            continue
          }

          const type = getChatType(channel)
          newChannels.push({ chatType: type, channelId: channel.id })
        }
        setChannels(newChannels)
      }
    } catch (error) {
      logger.error('💥 Error fetching channel:', error)
      // If we get a token error, try to reconnect
      if (error instanceof Error && error.message.includes('tokens are not set')) {
        await reconnect()
      }
    } finally {
      setIsLoading(false)
    }
  }, [client, currentUser?.id, userId, isConnecting, reconnect])

  useEffect(() => {
    fetchChannels()
  }, [fetchChannels])

  const handleChatClick = async (channelId?: string) => {
    try {
      if (!client || !currentUser?.id) {
        await reconnect()
        return
      }

      if (isConnecting) {
        return
      }

      // If we already have a channel ID, navigate to it
      if (channelId) {
        navigate(paths.chats.conversation.network.getHref(channelId))
        return
      }

      // For single channel, we can navigate directly
      if (channels.length === 1) {
        navigate(paths.chats.conversation.network.getHref(channels[0].channelId))
        return
      }

      // Otherwise, create or get a channel (fallback)
      setIsLoading(true)
      const channelList = await getChannels(client, String(currentUser.id), String(userId))

      if (!channelList || channelList.length === 0) {
        throw new Error('Channel not found')
      }

      const channel = channelList[0]
      if (!channel || !channel.id) {
        throw new Error('Channel not found')
      }

      navigate(paths.chats.conversation.network.getHref(channel.id))
    } catch (error) {
      logger.error('💥 Error handling chat click:', error)
      if (error instanceof Error && error.message.includes('tokens are not set')) {
        await reconnect()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSneakClick = async () => {
    try {
      setIsLoading(true)
      const chatId = await createSneakMutation.mutateAsync(String(userId))
      if (chatId) {
        setShowSneakConfirmation(false)
        navigate(paths.chats.conversation.sneak.getHref(chatId))
      }
    } catch (error) {
      logger.error('💥 Error creating sneak channel:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpgradeClick = () => {
    navigate(paths.subscriptions.getHref())
  }

  const hasChannels = channels.length > 0
  const hasSneakCredits = currentUser?.subscription?.sneakCredits && currentUser.subscription.sneakCredits > 0
  const hasSubscription = !!currentUser?.subscription
  const isClientConnected = !!client && !isConnecting

  // If client is not fully connected yet, show a disabled button
  if (!isClientConnected || isConnecting || isLoading || !targetUser) {
    return null
  }

  // If we have existing channels, show the normal chat button
  if (hasChannels) {
    return (
      <Button size="icon" onClick={() => handleChatClick(channels[0]?.channelId)} disabled={isLoading || isConnecting}>
        <ChatFilled size={18} className="text-white" />
      </Button>
    )
  }

  // For users with no existing channels, show the Sneak UI
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {!hasSubscription ? (
          <Button
            onClick={handleUpgradeClick}
            disabled={isLoading || isConnecting}
            className="flex w-full items-center justify-start gap-2 rounded-lg border border-sneak bg-sneak/10 px-3 py-1.5 text-primary"
          >
            <div className="flex size-8 items-center justify-center rounded-lg bg-sneak">
              <ChatFilled size={18} className="text-white" />
            </div>
            <div className="flex flex-col">
              <div className="text-left font-bold">Sneak</div>
              <div className="text-xs">Upgrade To Pro</div>
            </div>
          </Button>
        ) : !hasSneakCredits ? (
          <div className="flex items-center gap-2 rounded-lg border border-sneak bg-sneak/10 p-2">
            <div className="flex size-8 items-center justify-center rounded-lg bg-sneak">
              <ChatFilled size={18} className="text-white" />
            </div>
            <div className="flex flex-col">
              <div className="font-bold">Sneak</div>
              <div className="text-xs text-muted">Out of Credits</div>
            </div>
          </div>
        ) : (
          <Button
            onClick={() => setShowSneakConfirmation(true)}
            disabled={isLoading || isConnecting}
            className="flex w-full items-center justify-start gap-2 rounded-lg border border-sneak bg-sneak/10 px-3 py-1.5 text-primary"
          >
            <div className="flex size-8 items-center justify-center rounded-lg bg-sneak">
              <ChatFilled size={18} className="text-white" />
            </div>
            <div className="flex flex-col items-start">
              <div className="text-left font-bold">Sneak</div>
              <div className="text-xs">{currentUser.subscription?.sneakCredits} Credit(s)</div>
            </div>
          </Button>
        )}
      </div>

      <ConfirmSneakDialog
        open={showSneakConfirmation}
        onOpenChange={setShowSneakConfirmation}
        currentUser={currentUser}
        targetUser={targetUser}
        isLoading={isLoading}
        onConfirm={handleSneakClick}
      />
    </div>
  )
}
